import { useSyncExternalStore } from 'react';

/**
 * Point d'entrée UNIQUE du responsive de l'application : tout écran qui a besoin de connaître la
 * taille de la tablette passe par ici, jamais par son propre `window.innerWidth` — sans quoi les
 * seuils divergent d'un écran à l'autre au fil des retouches.
 *
 * Trois paliers, calés sur le parc réellement utilisé en atelier :
 *   compact  < 900   — 8,7" portrait (800 × 1340)
 *   moyen    < 1280  — 11" portrait (1024 × 1620)
 *   large   >= 1280  — 10,1" et 8,7" paysage (1280 × 800, 1340 × 800), 11" paysage (1640 × 1024)
 */
export const SEUIL_COMPACT = 900;
export const SEUIL_LARGE = 1280;

function mesurer() {
  const largeur = window.innerWidth;
  const hauteur = window.innerHeight;
  return {
    largeur,
    hauteur,
    palier: largeur < SEUIL_COMPACT ? 'compact' : largeur < SEUIL_LARGE ? 'moyen' : 'large',
    compact: largeur < SEUIL_COMPACT,
    moyen: largeur >= SEUIL_COMPACT && largeur < SEUIL_LARGE,
    large: largeur >= SEUIL_LARGE,
    // Une tablette carrée (rare) est comptée en paysage : c'est la disposition la plus sûre, elle
    // ne suppose pas de hauteur disponible.
    portrait: hauteur > largeur,
    paysage: hauteur <= largeur,
  };
}

// Un SEUL abonnement aux événements de fenêtre pour toute l'application, quel que soit le nombre
// de composants qui lisent le palier (une liste peut porter des dizaines de cartes). Le snapshot
// n'est remplacé que s'il change réellement, pour ne pas re-rendre à chaque pixel de redimension.
let instantane = typeof window === 'undefined' ? null : mesurer();
const abonnes = new Set();
let branche = false;

function identique(a, b) {
  return a.largeur === b.largeur && a.hauteur === b.hauteur;
}

function reevaluer() {
  const suivant = mesurer();
  if (instantane && identique(instantane, suivant)) return;
  instantane = suivant;
  abonnes.forEach((f) => f());
}

// `matchMedia` couvre les franchissements de seuil, `resize`/`orientationchange` le reste :
// rotation de la tablette et changements de hauteur (barre d'URL d'Android, clavier système),
// dont le pavé flottant de l'écran de saisie a besoin pour rester dans la zone visible.
const REQUETES = [`(max-width: ${SEUIL_COMPACT - 1}px)`, `(max-width: ${SEUIL_LARGE - 1}px)`];

function brancher() {
  if (branche) return;
  branche = true;
  window.addEventListener('resize', reevaluer);
  window.addEventListener('orientationchange', reevaluer);
  REQUETES.forEach((q) => {
    const mq = window.matchMedia(q);
    if (mq.addEventListener) mq.addEventListener('change', reevaluer);
    else mq.addListener(reevaluer); // WebView Android ancienne
  });
}

function abonner(surChangement) {
  brancher();
  abonnes.add(surChangement);
  return () => abonnes.delete(surChangement);
}

function lire() {
  if (!instantane) instantane = mesurer();
  return instantane;
}

/** @returns {{largeur:number, hauteur:number, palier:'compact'|'moyen'|'large', compact:boolean, moyen:boolean, large:boolean, portrait:boolean, paysage:boolean}} */
export default function useBreakpoint() {
  return useSyncExternalStore(abonner, lire, lire);
}
